/**
 * Who the signed-in person is currently viewing, if anyone.
 *
 * Kept apart from the auth store on purpose. The auth store answers "who am
 * I"; this answers "whose screens am I looking at". Folding the second into
 * the first would make every reader of `user` have to know which of the two
 * they meant, and the whole point of the top bar is that the distinction is
 * never in doubt.
 *
 * Session storage, not local: a view dies with the tab. The server sweeps
 * anything left open, so a closed tab is recorded as expired rather than
 * lingering as a live credential in a browser nobody is looking at.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { client, setViewAsToken } from '../api/client';

export interface ViewingTarget {
  userId: string;
  name: string;
  role: string;
  sessionId: string;
  token: string;
  mode: 'view' | 'act';
}

interface ImpersonationState {
  target: ViewingTarget | null;
  /** Why the last view ended, for the message shown after it does. */
  endedBecause: 'exited' | 'target_signed_in' | 'expired' | null;
  start: (target: ViewingTarget) => void;
  /** Ends the view on the server and locally. Safe to call twice. */
  stop: () => Promise<void>;
  /**
   * The target signed in, which invalidated the token underneath us.
   *
   * Ends the view without calling the server — the credential is already dead,
   * so the request would fail and the only thing it could achieve is replacing
   * a clear message with a confusing one. The server's own sweep closes the
   * session record.
   */
  endedByTargetSignIn: () => void;
  /** The thirty minutes ran out. Same handling, different sentence. */
  endedByExpiry: () => void;
  clearEnded: () => void;
}

export const useImpersonation = create<ImpersonationState>()(
  persist(
    (set, get) => ({
      target: null,
      endedBecause: null,

      start(target) {
        setViewAsToken(target.token);
        set({ target, endedBecause: null });
      },

      async stop() {
        const current = get().target;
        if (current) {
          try {
            await client.post('/api/v1/admin/view-as/end');
          } catch {
            // Best effort. A failed call must not trap somebody inside a view
            // they have asked to leave — the server sweeps it either way.
          }
        }
        setViewAsToken(null);
        set({ target: null, endedBecause: 'exited' });
      },

      endedByTargetSignIn() {
        setViewAsToken(null);
        set({ target: null, endedBecause: 'target_signed_in' });
      },

      endedByExpiry() {
        setViewAsToken(null);
        set({ target: null, endedBecause: 'expired' });
      },

      clearEnded() {
        set({ endedBecause: null });
      },
    }),
    {
      name: 'ats_viewing',
      storage: createJSONStorage(() => sessionStorage),
      // Rehydrating the token into the request layer, because that lives in a
      // module variable the store cannot reach from its initialiser.
      onRehydrateStorage: () => (state) => {
        if (state?.target) setViewAsToken(state.target.token);
      },
    },
  ),
);
